/**
 * SIM-2252: recently-used color memory for the ColorPicker — a small localStorage-backed MRU list
 * shared across every color control, so a color picked in one place is one click away in the next.
 * Only concrete `#rrggbb` values are tracked; `transparent`, CSS variables and rgb()/hsl() strings
 * are skipped, since they are not re-selectable as a swatch and would clutter the row.
 */
const STORAGE_KEY = "simplicity:recentColors";
const MAX_RECENTS = 8;
const HEX6 = /^#[0-9a-fA-F]{6}$/;

/** Read the recent-color MRU list (most-recent first). Safe in SSR / disabled
 *  storage — returns an empty list rather than throwing. */
export function getRecentColors(): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((c): c is string => typeof c === "string" && HEX6.test(c))
      .slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

/** Record a color as most-recently-used. No-ops for non-hex values and for
 *  storage errors (private mode, quota). Dedupes case-insensitively and caps
 *  the list at MAX_RECENTS. */
export function pushRecentColor(color: string): void {
  const hex = color.trim().toLowerCase();
  if (!HEX6.test(hex)) return;
  try {
    const next = [hex, ...getRecentColors().filter((c) => c.toLowerCase() !== hex)].slice(
      0,
      MAX_RECENTS,
    );
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // storage unavailable — recents simply won't persist this session.
  }
}

export { MAX_RECENTS as MAX_RECENT_COLORS };
